import { FC } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {
  FieldMapping,
  FieldMappingNodeData,
  FieldTransform,
  WorkflowNodeData,
} from '../types'
import { themeColor } from '../../../layout/theme/color'
import ExpressionTextField from '../autocomplete/ExpressionTextField'

const TRANSFORM_TYPES: { value: string; labelKey: string }[] = [
  { value: '', labelKey: 'workflow.config.fm.transformNone' },
  { value: 'uppercase', labelKey: 'workflow.config.fm.transformUppercase' },
  { value: 'lowercase', labelKey: 'workflow.config.fm.transformLowercase' },
  { value: 'trim', labelKey: 'workflow.config.fm.transformTrim' },
  { value: 'truncate', labelKey: 'workflow.config.fm.transformTruncate' },
  { value: 'default', labelKey: 'workflow.config.fm.transformDefault' },
  { value: 'lookup_table', labelKey: 'workflow.config.fm.transformLookup' },
  { value: 'to_number', labelKey: 'workflow.config.fm.transformToNumber' },
  { value: 'to_boolean', labelKey: 'workflow.config.fm.transformToBoolean' },
  { value: 'regex_replace', labelKey: 'workflow.config.fm.transformRegexReplace' },
]

interface FieldMappingConfigProps {
  data: FieldMappingNodeData
  onUpdate: (data: Partial<WorkflowNodeData>) => void
}

const FieldMappingConfig: FC<FieldMappingConfigProps> = ({ data, onUpdate }) => {
  const translate = useTranslate()
  const mappings = data.mappings ?? []

  const updateMapping = (index: number, patch: Partial<FieldMapping>) => {
    const next = mappings.map((m, i) => (i === index ? { ...m, ...patch } : m))
    onUpdate({ mappings: next })
  }

  const updateTransform = (index: number, patch: Partial<FieldTransform>) => {
    // eslint-disable-next-line security/detect-object-injection
    const target = mappings[index]
    if (!target) return
    const current = target.transform ?? {}
    const merged: FieldTransform = { ...current, ...patch }
    // Empty transform.type → drop the field; backend identity-maps the value.
    const next: FieldMapping = { ...target }
    if (merged.type) {
      next.transform = merged
    } else {
      delete next.transform
    }
    updateMapping(index, next)
  }

  const handleAdd = () => {
    onUpdate({ mappings: [...mappings, { from: '', to: '' }] })
  }

  const handleRemove = (index: number) => {
    onUpdate({ mappings: mappings.filter((_, i) => i !== index) })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label={translate('workflow.config.label')}
        value={data.label}
        onChange={(e) => onUpdate({ label: e.target.value })}
        size="small"
        fullWidth
      />

      <ExpressionTextField
        label={translate('workflow.config.fm.inputSource')}
        helperText={translate('workflow.config.fm.inputSourceHelp')}
        value={data.inputSource ?? ''}
        onChange={(e) => onUpdate({ inputSource: e.target.value })}
        size="small"
        fullWidth
      />

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: themeColor.slate60 }}
          >
            {translate('workflow.config.fm.mappings')}
          </Typography>
          <Button
            startIcon={<AddIcon fontSize="small" />}
            size="small"
            onClick={handleAdd}
          >
            {translate('workflow.config.fm.addMapping')}
          </Button>
        </Box>

        {mappings.length === 0 && (
          <Typography variant="caption" sx={{ color: themeColor.slate60, fontStyle: 'italic' }}>
            {translate('workflow.config.fm.noMappings')}
          </Typography>
        )}

        {mappings.map((mapping, index) => {
          const transform = mapping.transform
          const transformType = transform?.type ?? ''
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '8px',
                p: 1.5,
                mb: 1,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <ExpressionTextField
                  label={translate('workflow.config.fm.from')}
                  value={mapping.from}
                  onChange={(e) => updateMapping(index, { from: e.target.value })}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label={translate('workflow.config.fm.to')}
                  value={mapping.to}
                  onChange={(e) => updateMapping(index, { to: e.target.value })}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => handleRemove(index)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  select
                  label={translate('workflow.config.fm.transform')}
                  value={transformType}
                  onChange={(e) =>
                    updateTransform(index, {
                      type: e.target.value as FieldTransform['type'],
                    })
                  }
                  size="small"
                  sx={{ flex: 1 }}
                >
                  {TRANSFORM_TYPES.map((t) => (
                    <MenuItem key={t.value || 'none'} value={t.value}>
                      {translate(t.labelKey)}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!mapping.required}
                      onChange={(e) => updateMapping(index, { required: e.target.checked })}
                    />
                  }
                  label={
                    <Typography variant="caption">
                      {translate('workflow.config.fm.required')}
                    </Typography>
                  }
                />
              </Box>

              {transformType === 'truncate' && (
                <TextField
                  label={translate('workflow.config.fm.maxLength')}
                  type="number"
                  value={transform?.maxLength ?? ''}
                  onChange={(e) =>
                    updateTransform(index, { maxLength: parseInt(e.target.value, 10) || 0 })
                  }
                  size="small"
                />
              )}
              {transformType === 'default' && (
                <TextField
                  label={translate('workflow.config.fm.defaultValue')}
                  value={transform?.defaultValue ?? ''}
                  onChange={(e) => updateTransform(index, { defaultValue: e.target.value })}
                  size="small"
                />
              )}
              {transformType === 'lookup_table' && (
                <TextField
                  label={translate('workflow.config.fm.lookupTable')}
                  helperText={translate('workflow.config.fm.lookupTableHelp')}
                  value={JSON.stringify(transform?.lookupTable ?? {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value || '{}') as Record<
                        string,
                        string | number | boolean
                      >
                      updateTransform(index, { lookupTable: parsed })
                    } catch {
                      // do nothing
                    }
                  }}
                  size="small"
                  multiline
                  minRows={2}
                />
              )}
              {transformType === 'regex_replace' && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label={translate('workflow.config.fm.regexPattern')}
                    value={transform?.pattern ?? ''}
                    onChange={(e) => updateTransform(index, { pattern: e.target.value })}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label={translate('workflow.config.fm.regexReplacement')}
                    value={transform?.replacement ?? ''}
                    onChange={(e) => updateTransform(index, { replacement: e.target.value })}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default FieldMappingConfig
